import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Demographics } from "@/components/hr/Demographics";
import AppLayout from "@/components/layout/AppLayout";
import { useLanguage } from "@/contexts/LanguageContext";

export default function DemographicsPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();

  // Translation function for demographics page
  const getTranslation = (key: string) => {
    const translations = {
      // Page translations
      backToHrManagement: {
        en: "Back to Human Resources Management",
        el: "Επιστροφή στη Διαχείριση Ανθρώπινου Δυναμικού"
      },
      demographicsTitle: {
        en: "Demographics & Statistics", 
        el: "Δημογραφικά Στοιχεία & Στατιστικές"
      },
      demographicsDescription: {
        en: "Demographic data, personnel analysis statistics, satisfaction, complaints and fatigue levels",
        el: "Δημογραφικά στοιχεία, στατιστικές ανάλυσης προσωπικού, ικανοποίηση, παράπονα και επίπεδα κούρασης"
      },
      // Filter section
      dataFilters: {
        en: "Data Filters",
        el: "Φίλτρα Δεδομένων"
      },
      showDataFor: {
        en: "Show data for:",
        el: "Εμφάνιση δεδομένων για:"
      },
      all: {
        en: "All",
        el: "Όλους"
      },
      guards: {
        en: "Guards",
        el: "Φύλακες"
      },
      admins: {
        en: "Administrators",
        el: "Διαχειριστές"
      },
      team: {
        en: "Team:",
        el: "Ομάδα:"
      },
      allTeams: {
        en: "All teams",
        el: "Όλες οι ομάδες"
      },
      // Stats cards
      activeEmployees: {
        en: "Active Employees",
        el: "Ενεργοί Εργαζόμενοι"
      },
      avgTenure: {
        en: "Average Tenure",
        el: "Μέση Προϋπηρεσία"
      },
      turnoverRate: {
        en: "Turnover Rate",
        el: "Ποσοστό Εκροής"
      },
      avgBusinessExperience: {
        en: "Average Business Experience",
        el: "Μέση Επιχειρηματική Εμπειρία"
      },
      avgSatisfaction: {
        en: "Average Satisfaction",
        el: "Μέση Ικανοποίηση"
      },
      last30Days: {
        en: "last 30 days",
        el: "τελευταίες 30 ημέρες"
      },
      complaints90d: {
        en: "Complaints (90d)",
        el: "Παράπονα (90 ημ.)"
      },
      pending: {
        en: "pending",
        el: "σε εκκρεμότητα"
      },
      avgFatigueLevel: {
        en: "Average Fatigue Level",
        el: "Μέσο Επίπεδο Κόπωσης"
      },
      complaintResolutionRate: {
        en: "Complaint Resolution Rate",
        el: "Βαθμός Επίλυσης Παραπόνων"
      },
      target: {
        en: "target",
        el: "στόχος"
      },
      withSatisfaction: {
        en: "With satisfaction",
        el: "Με ικανοποίηση"
      },
      withoutSatisfaction: {
        en: "Without satisfaction",
        el: "Χωρίς ικανοποίηση"
      },
      onTarget: {
        en: "On Target",
        el: "Στόχος"
      },
      belowTarget: {
        en: "Below Target",
        el: "Κάτω στόχου"
      },
      // Chart titles
      genderDistribution: {
        en: "Gender Distribution",
        el: "Κατανομή Φύλου"
      },
      ageDistribution: {
        en: "Age Distribution",
        el: "Κατανομή Ηλικίας"
      },
      tenureDistribution: {
        en: "Tenure Distribution",
        el: "Κατανομή Προϋπηρεσίας"
      },
      turnoverTrends: {
        en: "Turnover Trends",
        el: "Τάσεις Εκροής"
      },
      // Chart labels
      male: {
        en: "Male",
        el: "Άνδρες"
      },
      female: {
        en: "Female",
        el: "Γυναίκες"
      },
      hires: {
        en: "Hires",
        el: "Προσλήψεις"
      },
      departures: {
        en: "Departures",
        el: "Αποχωρήσεις"
      },
      years: {
        en: "years",
        el: "έτη"
      }
    };

    const translation = translations[key as keyof typeof translations];
    return translation?.[language as keyof typeof translation] || translation?.en || key;
  };

  return (
    <AppLayout>
      <div className="space-y-6 px-[30px]">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/hr")}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {getTranslation("backToHrManagement")}
          </Button>
        </div>
        
        <div className="space-y-2">
          <h1 className="text-3xl font-bold">{getTranslation("demographicsTitle")}</h1>
          <p className="text-muted-foreground">
            {getTranslation("demographicsDescription")}
          </p>
        </div>

        <Demographics t={getTranslation} />
      </div>
    </AppLayout>
  );
}